import React, { useState, useRef, useEffect, DragEvent, ChangeEvent } from "react";
import api from "../services/api";
import { normalizeImageForUpload } from "../lib/imageFile";

export type ImageUploadProps = {
  value?: string;
  onChange: (url: string) => void;
  endpoint?: "club-logo" | "avatar" | "reward-image" | "image";
  label?: string;
  maxSizeMB?: number;
  previewClassName?: string;
  disabled?: boolean;
};

export default function ImageUpload({
  value,
  onChange,
  endpoint = "image",
  label = "Upload Image",
  maxSizeMB = 10,
  previewClassName = "w-32 h-32",
  disabled = false,
}: ImageUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(value || null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Keep the preview in sync when the stored value arrives (or changes) after
  // mount — e.g. admin settings load the saved logo/OG-image asynchronously, so
  // the dropzone must show the existing image instead of an empty state.
  useEffect(() => {
    setPreview(value || null);
  }, [value]);

  const uploadFile = async (picked: File) => {
    try {
      setIsUploading(true);
      setError(null);

      // Decode + re-encode the picked file. This converts a phone camera's
      // HEIC/HEIF into JPEG and shrinks oversized photos, and it means we do
      // NOT gate on file.type — Android's picker frequently reports no MIME
      // type at all for a perfectly ordinary image. The server validates the
      // actual bytes.
      const file = await normalizeImageForUpload(picked);

      const maxBytes = maxSizeMB * 1024 * 1024;
      if (file.size > maxBytes) {
        setError(`File size must be less than ${maxSizeMB}MB`);
        return;
      }

      // Create FormData
      const formData = new FormData();
      formData.append("file", file);

      // Upload to backend
      const { data } = await api.post(`/upload/${endpoint}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      // Use optimized URL or fallback to url
      const imageUrl = data.url || data.optimized;

      if (imageUrl) {
        setPreview(imageUrl);
        onChange(imageUrl);
      } else {
        throw new Error("No URL returned from upload");
      }
    } catch (err: any) {
      console.error("Upload failed:", err);
      setError(err.response?.data?.error || "Failed to upload image");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      uploadFile(files[0]);
    }
  };

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      uploadFile(files[0]);
    }
  };

  const handleClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPreview(null);
    onChange("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      {/* slate-700, not the dark palette's near-white text-ink-100, which was
          invisible on the white admin forms this control is used in. */}
      {label && (
        <label className="block text-sm font-medium text-slate-700 mb-2">
          {label}
        </label>
      )}

      <div
        className={`relative border-2 border-dashed rounded-lg transition-all cursor-pointer ${
          isDragging
            ? "border-brand-500 bg-brand-500/10"
            : preview
            ? "border-glass-700 bg-night-900"
            : "border-glass-700 bg-night-900 hover:border-brand-500/50"
        } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
          disabled={disabled}
        />

        {isUploading ? (
          <div className="flex flex-col items-center justify-center p-8">
            <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin mb-3"></div>
            <p className="text-sm text-ink-300">Uploading...</p>
          </div>
        ) : preview ? (
          <div className="relative p-4 flex flex-col items-center">
            <img
              src={preview}
              alt="Preview"
              className={`${previewClassName} object-cover rounded-lg border border-glass-700`}
            />
            {!disabled && (
              <button
                onClick={handleRemove}
                className="mt-3 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors text-sm"
              >
                Remove
              </button>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center p-8">
            <svg
              className="w-12 h-12 text-ink-400 mb-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <p className="text-sm text-ink-200 mb-1">
              Drag & drop image here
            </p>
            <p className="text-xs text-ink-400 mb-3">or click to browse</p>
            <p className="text-xs text-ink-500">
              Max size: {maxSizeMB}MB
            </p>
          </div>
        )}
      </div>

      {error && (
        <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
          {error}
        </div>
      )}
    </div>
  );
}
